import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Container, Typography, Button, Grid, Chip, Rating, Avatar } from "@mui/material";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { keyframes } from "@mui/system";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import CodeIcon from "@mui/icons-material/Code";
import CloudIcon from "@mui/icons-material/Cloud";
import EastIcon from "@mui/icons-material/East";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DataObjectIcon from "@mui/icons-material/DataObject";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

// ── Keyframes ─────────────────────────────────────────────────────────────────
const gradientShift  = keyframes`0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}`;
const float          = keyframes`0%,100%{transform:translateY(0px)}50%{transform:translateY(-18px)}`;
const floatX         = keyframes`0%,100%{transform:translateX(0px)}50%{transform:translateX(12px)}`;
const floatReverse   = keyframes`0%,100%{transform:translateY(0px)}50%{transform:translateY(18px)}`;
const rotate3d       = keyframes`from{transform:perspective(800px) rotateY(0deg) rotateX(5deg)}to{transform:perspective(800px) rotateY(360deg) rotateX(5deg)}`;
const orbitSlow      = keyframes`from{transform:rotate(0deg) translateX(120px) rotate(0deg)}to{transform:rotate(360deg) translateX(120px) rotate(-360deg)}`;
const orbitFast      = keyframes`from{transform:rotate(0deg) translateX(80px) rotate(0deg)}to{transform:rotate(-360deg) translateX(80px) rotate(360deg)}`;
const pulse          = keyframes`0%,100%{transform:scale(1);opacity:0.6}50%{transform:scale(1.15);opacity:1}`;
const shimmer        = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;
const slideInLeft    = keyframes`from{opacity:0;transform:translateX(-80px)}to{opacity:1;transform:translateX(0)}`;
const slideInRight   = keyframes`from{opacity:0;transform:translateX(80px)}to{opacity:1;transform:translateX(0)}`;
const fadeInUp       = keyframes`from{opacity:0;transform:translateY(50px)}to{opacity:1;transform:translateY(0)}`;
const marquee        = keyframes`0%{transform:translateX(0)}100%{transform:translateX(-50%)}`;
const glow           = keyframes`0%,100%{box-shadow:0 0 20px rgba(100,181,246,0.3)}50%{box-shadow:0 0 60px rgba(100,181,246,0.9),0 0 100px rgba(25,118,210,0.5)}`;
const glowGreen      = keyframes`0%,100%{box-shadow:0 0 10px rgba(76,175,80,0.4)}50%{box-shadow:0 0 30px rgba(76,175,80,0.9)}`;
const spin           = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const spinReverse    = keyframes`from{transform:rotate(360deg)}to{transform:rotate(0deg)}`;
const typewriter     = keyframes`from{width:0}to{width:100%}`;
const blink          = keyframes`0%,100%{opacity:1}50%{opacity:0}`;
const morphShape     = keyframes`0%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%}100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}`;
const tiltCard       = keyframes`0%,100%{transform:perspective(1000px) rotateX(0deg) rotateY(0deg)}25%{transform:perspective(1000px) rotateX(3deg) rotateY(3deg)}75%{transform:perspective(1000px) rotateX(-3deg) rotateY(-3deg)}`;
const scanLine       = keyframes`0%{top:-100%}100%{top:200%}`;
const ripple         = keyframes`0%{transform:scale(0);opacity:1}100%{transform:scale(4);opacity:0}`;

// ── Interactive Particle Canvas ───────────────────────────────────────────────
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const mouseRef  = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.6,
      vy: (Math.random() - 0.5) * 0.6,
      r: Math.random() * 2.5 + 0.5,
      opacity: Math.random() * 0.6 + 0.2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147,197,253,${p.opacity})`;
        ctx.fill();
      });
      // connect nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(147,197,253,${0.15 * (1 - d / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      // mouse attract
      const mx = mouseRef.current.x, my = mouseRef.current.y;
      particles.forEach(p => {
        const dx = mx - p.x, dy = my - p.y;
        const d  = Math.sqrt(dx * dx + dy * dy);
        if (d < 150) { p.x += dx * 0.01; p.y += dy * 0.01; }
      });
      animRef.current = requestAnimationFrame(draw);
    };

    const onMouse = e => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    canvas.addEventListener("mousemove", onMouse);
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "all" }} />;
};

// ── Tilt Card ─────────────────────────────────────────────────────────────────
const TiltCard = ({ children, sx = {} }) => {
  const ref  = useRef(null);
  const onMove = e => {
    const el  = ref.current; if (!el) return;
    const r   = el.getBoundingClientRect();
    const x   = (e.clientX - r.left) / r.width  - 0.5;
    const y   = (e.clientY - r.top)  / r.height - 0.5;
    el.style.transform = `perspective(1000px) rotateX(${-y * 14}deg) rotateY(${x * 14}deg) scale(1.03)`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)"; };
  return <Box ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} sx={{ transition: "transform 0.1s ease", transformStyle: "preserve-3d", ...sx }}>{children}</Box>;
};

// ── Animated Counter ──────────────────────────────────────────────────────────
const Counter = ({ target, inView }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const n = parseInt(target.replace(/\D/g, ""));
    let cur = 0; const step = n / 60;
    const t = setInterval(() => { cur = Math.min(cur + step, n); setV(Math.floor(cur)); if (cur >= n) clearInterval(t); }, 33);
    return () => clearInterval(t);
  }, [inView, target]);
  return <>{v}{target.includes("+") ? "+" : target.includes("%") ? "%" : ""}</>;
};

// ── Data ──────────────────────────────────────────────────────────────────────
const SERVICES = [
  { icon: <SchoolIcon sx={{ fontSize: 44 }} />, title: "Corporate Training", desc: "Transform your team with cutting-edge technology programs.", tags: ["React", "Cloud", "AI/ML", "DevOps"], color: "#1565C0" },
  { icon: <GroupsIcon sx={{ fontSize: 44 }} />, title: "IT Staffing", desc: "Access pre-vetted tech talent with flexible engagement models.", tags: ["Contract", "Permanent", "Remote", "Teams"], color: "#0D47A1" },
  { icon: <CodeIcon sx={{ fontSize: 44 }} />, title: "Software Development", desc: "End-to-end custom solutions built with modern tech stacks.", tags: ["Web", "Mobile", "SaaS", "APIs"], color: "#1976D2" },
  { icon: <CloudIcon sx={{ fontSize: 44 }} />, title: "Cloud Solutions", desc: "Seamless cloud transformation and infrastructure management.", tags: ["AWS", "Azure", "GCP", "K8s"], color: "#0277BD" },
  { icon: <DataObjectIcon sx={{ fontSize: 44 }} />, title: "Data & Analytics", desc: "Transform raw data into actionable business intelligence.", tags: ["BI", "ETL", "Big Data", "ML"], color: "#1565C0" },
  { icon: <IntegrationInstructionsIcon sx={{ fontSize: 44 }} />, title: "DevOps & Automation", desc: "Streamline delivery with CI/CD pipelines and automation.", tags: ["CI/CD", "Docker", "GitOps", "IaC"], color: "#0D47A1" },
];

const STATS = [
  { n: "850+", label: "Projects Delivered", icon: <AutoGraphIcon sx={{ fontSize: 32 }} />, color: "#1565C0" },
  { n: "450+", label: "Happy Clients",       icon: <WorkspacePremiumIcon sx={{ fontSize: 32 }} />, color: "#0D47A1" },
  { n: "98%",  label: "Satisfaction",        icon: <TrendingUpIcon sx={{ fontSize: 32 }} />, color: "#1976D2" },
  { n: "15+",  label: "Years Experience",    icon: <SpeedIcon sx={{ fontSize: 32 }} />, color: "#0277BD" },
];

const CLIENTS = ["Toyota","Santander","Fidelity","BristleCone","Apex Systems","Orcasio","Shipium","Eliassen","SGT","Turnberry","BenefitMall","Beacon Hill","Lions Club","IG Group"];

const TESTIMONIALS = [
  { name: "Sarah Johnson", pos: "CTO, TechCorp Solutions",    av: "SJ", text: "Quantum IT AI LLC transformed our development process. Robust cloud solution that scaled perfectly with our growth." },
  { name: "Michael Chen",  pos: "VP Engineering, DataFlow",   av: "MC", text: "The training program was exceptional. Our developers gained skills that improved productivity by 40% immediately." },
  { name: "Emily Rodriguez",pos: "Director IT, FinServe Global",av:"ER",text: "Outstanding staffing! They connected us with top-tier developers who delivered results from day one." },
];

// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const { ref: statsRef,        inView: statsInView }        = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: servicesRef,     inView: servicesInView }     = useInView({ triggerOnce: true, threshold: 0.05 });
  const { ref: whyRef,          inView: whyInView }          = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: testimonialsRef, inView: testimonialsInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: ctaRef,          inView: ctaInView }          = useInView({ triggerOnce: true, threshold: 0.1 });

  // Typed text effect
  const words = ["Software Development", "IT Staffing", "Cloud Solutions", "AI Training", "Digital Transformation"];
  const [wordIdx, setWordIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const word = words[wordIdx];
    const speed = deleting ? 40 : 80;
    const timer = setTimeout(() => {
      if (!deleting && displayed.length < word.length) {
        setDisplayed(word.slice(0, displayed.length + 1));
      } else if (deleting && displayed.length > 0) {
        setDisplayed(word.slice(0, displayed.length - 1));
      } else if (!deleting) {
        setTimeout(() => setDeleting(true), 1500);
      } else {
        setDeleting(false);
        setWordIdx(i => (i + 1) % words.length);
      }
    }, speed);
    return () => clearTimeout(timer);
  }, [displayed, deleting, wordIdx]);

  return (
    <Box sx={{ overflow: "hidden" }}>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — Full‑screen 3D interactive
      ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        minHeight: "100vh", position: "relative",
        display: "flex", alignItems: "center",
        overflow: "hidden",
        background: "linear-gradient(-45deg,#020818,#0a1628,#0D47A1,#0a1628,#020818,#1565C0)",
        backgroundSize: "600% 600%",
        animation: `${gradientShift} 10s ease infinite`,
      }}>
        {/* Interactive particle canvas */}
        <ParticleCanvas />

        {/* Grid overlay */}
        <Box sx={{ position:"absolute",inset:0,opacity:0.04,
          backgroundImage:"linear-gradient(rgba(100,181,246,0.4) 1px,transparent 1px),linear-gradient(90deg,rgba(100,181,246,0.4) 1px,transparent 1px)",
          backgroundSize:"60px 60px" }} />

        {/* Scan‑line effect */}
        <Box sx={{ position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none",zIndex:1 }}>
          <Box sx={{ position:"absolute",left:0,right:0,height:"2px",
            background:"linear-gradient(90deg,transparent,rgba(100,181,246,0.4),transparent)",
            animation:`${scanLine} 6s linear infinite` }} />
        </Box>

        {/* 3‑D orbiting rings */}
        <Box sx={{ position:"absolute",top:"50%",right:"8%",transform:"translateY(-50%)",
          width:280,height:280,pointerEvents:"none",zIndex:1 }}>
          {/* outer ring */}
          <Box sx={{ position:"absolute",inset:0,border:"1px solid rgba(100,181,246,0.2)",
            borderRadius:"50%",animation:`${spin} 20s linear infinite` }}>
            <Box sx={{ position:"absolute",top:-6,left:"50%",transform:"translateX(-50%)",
              width:12,height:12,borderRadius:"50%",bgcolor:"#64B5F6",
              boxShadow:"0 0 10px #64B5F6", animation:`${pulse} 2s ease-in-out infinite` }} />
          </Box>
          {/* middle ring */}
          <Box sx={{ position:"absolute",inset:30,border:"1px solid rgba(147,197,253,0.15)",
            borderRadius:"50%",animation:`${spinReverse} 14s linear infinite` }}>
            <Box sx={{ position:"absolute",top:-5,left:"50%",transform:"translateX(-50%)",
              width:10,height:10,borderRadius:"50%",bgcolor:"#90CAF9",
              boxShadow:"0 0 8px #90CAF9" }} />
          </Box>
          {/* inner morph blob */}
          <Box sx={{ position:"absolute",inset:60,
            background:"radial-gradient(circle,rgba(25,118,210,0.4),rgba(13,71,161,0.1))",
            animation:`${morphShape} 8s ease-in-out infinite`,
            boxShadow:"0 0 60px rgba(25,118,210,0.4)" }} />
          {/* core */}
          <Box sx={{ position:"absolute",inset:100,borderRadius:"50%",
            background:"linear-gradient(135deg,#1976D2,#0D47A1)",
            boxShadow:"0 0 40px rgba(25,118,210,0.8),0 0 80px rgba(13,71,161,0.4)",
            animation:`${glow} 3s ease-in-out infinite`,
            display:"flex",alignItems:"center",justifyContent:"center" }}>
            <Typography sx={{ color:"white",fontWeight:900,fontSize:"0.7rem",textAlign:"center",lineHeight:1.2 }}>
              AI<br/>TECH
            </Typography>
          </Box>
        </Box>

        {/* Floating 3D shapes */}
        {[
          { w:80,h:80,top:"12%",left:"6%",anim:float,dur:"7s",opacity:0.12 },
          { w:50,h:50,top:"70%",left:"4%",anim:floatReverse,dur:"5s",opacity:0.08 },
          { w:100,h:100,bottom:"8%",right:"30%",anim:floatX,dur:"9s",opacity:0.07 },
        ].map((s,i)=>(
          <Box key={i} sx={{
            position:"absolute",width:s.w,height:s.h,
            top:s.top,left:s.left,bottom:s.bottom,right:s.right,
            border:"1px solid rgba(100,181,246,0.3)",
            borderRadius:"30% 70% 70% 30%/30% 30% 70% 70%",
            background:"linear-gradient(135deg,rgba(25,118,210,0.15),rgba(255,255,255,0.03))",
            animation:`${s.anim} ${s.dur} ease-in-out infinite`,
            opacity:s.opacity,pointerEvents:"none",
          }} />
        ))}

        {/* Main content */}
        <Container maxWidth="lg" sx={{ position:"relative",zIndex:2,pt:14,pb:10 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ animation:`${slideInLeft} 0.9s ease-out both` }}>
                {/* Live badge */}
                <Box sx={{ display:"inline-flex",alignItems:"center",gap:1,
                  px:2.5,py:1,mb:3,borderRadius:"50px",
                  background:"rgba(255,255,255,0.06)",
                  backdropFilter:"blur(20px)",
                  border:"1px solid rgba(100,181,246,0.3)",
                  animation:`${glow} 4s ease-in-out infinite` }}>
                  <Box sx={{ width:8,height:8,borderRadius:"50%",bgcolor:"#4CAF50",
                    animation:`${pulse} 1.5s ease-in-out infinite`,
                    boxShadow:"0 0 10px #4CAF50" }} />
                  <Typography sx={{ color:"rgba(255,255,255,0.9)",fontSize:"0.82rem",fontWeight:600,letterSpacing:"0.06em" }}>
                    🚀 Trusted by 450+ Companies Worldwide
                  </Typography>
                </Box>

                {/* Heading */}
                <Typography sx={{ fontSize:{xs:"2.6rem",md:"4.2rem"},fontWeight:900,
                  lineHeight:1.05,mb:1.5,color:"white",letterSpacing:"-0.02em" }}>
                  Transforming
                </Typography>
                <Box sx={{
                  fontSize:{xs:"2.6rem",md:"4.2rem"},fontWeight:900,lineHeight:1.05,mb:1.5,
                  background:"linear-gradient(90deg,#fff 0%,#93C5FD 30%,#fff 60%,#60A5FA 100%)",
                  backgroundSize:"200% auto",backgroundClip:"text",
                  WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
                  animation:`${shimmer} 3s linear infinite`,display:"inline-block",
                }}>
                  Businesses
                </Box>
                {/* Typewriter */}
                <Box sx={{ display:"flex",alignItems:"center",gap:1,mb:3,minHeight:56 }}>
                  <Typography sx={{ fontSize:{xs:"1.1rem",md:"1.5rem"},fontWeight:700,color:"#93C5FD" }}>with</Typography>
                  <Typography sx={{ fontSize:{xs:"1.1rem",md:"1.5rem"},fontWeight:700,
                    color:"white",borderRight:"3px solid #60A5FA",pr:0.5,
                    animation:`${blink} 1s step-end infinite` }}>
                    {displayed}
                  </Typography>
                </Box>

                <Typography sx={{ mb:4,color:"rgba(255,255,255,0.8)",lineHeight:1.8,maxWidth:520,fontSize:"1rem" }}>
                  Quantum IT AI LLC delivers world-class software development, expert training, and strategic staffing to accelerate your digital transformation journey.
                </Typography>

                <Box sx={{ display:"flex",gap:2,flexWrap:"wrap",mb:4 }}>
                  <Button component={Link} to="/contact" variant="contained" size="large" endIcon={<EastIcon />}
                    sx={{ px:4,py:1.8,fontSize:"1rem",fontWeight:700,
                      background:"linear-gradient(135deg,#fff,#E3F2FD)",
                      color:"#0D47A1",borderRadius:"50px",
                      boxShadow:"0 8px 32px rgba(0,0,0,0.4)",
                      "&:hover":{transform:"translateY(-5px) scale(1.03)",
                        boxShadow:"0 20px 60px rgba(0,0,0,0.5)"} }}>
                    Start Your Project
                  </Button>
                  <Button component={Link} to="/services" variant="outlined" size="large"
                    sx={{ px:4,py:1.8,fontSize:"1rem",fontWeight:600,
                      borderColor:"rgba(147,197,253,0.6)",color:"white",
                      borderRadius:"50px",borderWidth:2,
                      backdropFilter:"blur(10px)",background:"rgba(255,255,255,0.05)",
                      "&:hover":{borderWidth:2,background:"rgba(255,255,255,0.12)",transform:"translateY(-5px)"} }}>
                    Explore Services
                  </Button>
                </Box>

                {/* Trust row */}
                <Box sx={{ display:"flex",gap:3,flexWrap:"wrap" }}>
                  {["ISO Certified","5★ Rated","24/7 Support","< 1hr Response"].map((t,i)=>(
                    <Box key={i} sx={{ display:"flex",alignItems:"center",gap:0.8 }}>
                      <CheckCircleIcon sx={{ color:"#4CAF50",fontSize:16,
                        animation:`${glowGreen} 2s ease-in-out infinite`,animationDelay:`${i*0.3}s` }} />
                      <Typography sx={{ color:"rgba(255,255,255,0.75)",fontSize:"0.8rem",fontWeight:500 }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right — 3D glass dashboard */}
            <Grid item xs={12} md={6}>
              <Box sx={{ animation:`${slideInRight} 0.9s ease-out 0.25s both`,
                display:"flex",justifyContent:"center" }}>
                <TiltCard sx={{ position:"relative",animation:`${float} 7s ease-in-out infinite` }}>
                  {/* Main glass card */}
                  <Box sx={{ width:{xs:300,md:360},p:4,borderRadius:4,
                    background:"rgba(255,255,255,0.07)",backdropFilter:"blur(30px)",
                    border:"1px solid rgba(255,255,255,0.15)",
                    boxShadow:"0 30px 80px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.15)",
                    position:"relative",overflow:"hidden" }}>
                    {/* holographic shimmer strip */}
                    <Box sx={{ position:"absolute",top:0,left:0,right:0,height:"1px",
                      background:"linear-gradient(90deg,transparent,rgba(147,197,253,0.8),transparent)",
                      animation:`${shimmer} 3s linear infinite` }} />

                    <Box sx={{ display:"flex",alignItems:"center",gap:1.5,mb:3 }}>
                      <Box sx={{ width:8,height:8,borderRadius:"50%",bgcolor:"#4CAF50",
                        animation:`${pulse} 1.5s ease-in-out infinite`,boxShadow:"0 0 10px #4CAF50" }} />
                      <Typography sx={{ color:"rgba(255,255,255,0.6)",fontSize:"0.72rem",
                        letterSpacing:"0.15em",textTransform:"uppercase" }}>Live Dashboard</Typography>
                    </Box>

                    {[
                      { label:"Client Satisfaction", v:98, color:"#4CAF50" },
                      { label:"Project Success",     v:96, color:"#2196F3" },
                      { label:"On-Time Delivery",    v:94, color:"#FF9800" },
                      { label:"Team Performance",    v:99, color:"#9C27B0" },
                    ].map((item,i)=>(
                      <Box key={i} sx={{ mb:2.5 }}>
                        <Box sx={{ display:"flex",justifyContent:"space-between",mb:0.5 }}>
                          <Typography sx={{ color:"rgba(255,255,255,0.75)",fontSize:"0.78rem" }}>{item.label}</Typography>
                          <Typography sx={{ color:"white",fontSize:"0.78rem",fontWeight:700 }}>{item.v}%</Typography>
                        </Box>
                        <Box sx={{ height:6,borderRadius:3,bgcolor:"rgba(255,255,255,0.08)",overflow:"hidden" }}>
                          <Box sx={{ height:"100%",borderRadius:3,width:`${item.v}%`,
                            background:`linear-gradient(90deg,${item.color}88,${item.color})`,
                            boxShadow:`0 0 12px ${item.color}`,
                            transition:"width 1.5s ease" }} />
                        </Box>
                      </Box>
                    ))}

                    <Box sx={{ mt:3,pt:2.5,borderTop:"1px solid rgba(255,255,255,0.08)",
                      display:"flex",justifyContent:"space-between" }}>
                      {[["850+","Projects"],["450+","Clients"],["15+","Years"]].map(([n,l],i)=>(
                        <Box key={i} sx={{ textAlign:"center" }}>
                          <Typography sx={{ color:"white",fontWeight:900,fontSize:"1.6rem",
                            textShadow:"0 0 20px rgba(147,197,253,0.6)" }}>{n}</Typography>
                          <Typography sx={{ color:"rgba(255,255,255,0.5)",fontSize:"0.65rem",textTransform:"uppercase",letterSpacing:"0.1em" }}>{l}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Floating mini chips */}
                  {[
                    { text:"⬆ 40% Growth",  bg:"rgba(76,175,80,0.92)",  t:-22,r:-30,anim:floatReverse,dur:"4s" },
                    { text:"🏆 Top Rated",   bg:"rgba(255,152,0,0.92)",  b:-18,l:-25,anim:float,dur:"5s" },
                    { text:"⚡ Agile",        bg:"rgba(33,150,243,0.92)", t:"40%",r:-45,anim:floatX,dur:"6s" },
                  ].map((c,i)=>(
                    <Box key={i} sx={{
                      position:"absolute",px:2,py:1,borderRadius:2,whiteSpace:"nowrap",
                      background:c.bg,backdropFilter:"blur(10px)",
                      top:c.t,right:c.r,bottom:c.b,left:c.l,
                      animation:`${c.anim} ${c.dur} ease-in-out infinite`,
                      boxShadow:`0 8px 24px rgba(0,0,0,0.4)`,
                    }}>
                      <Typography sx={{ color:"white",fontSize:"0.72rem",fontWeight:700 }}>{c.text}</Typography>
                    </Box>
                  ))}
                </TiltCard>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Wave */}
        <Box sx={{ position:"absolute",bottom:-2,left:0,right:0,zIndex:2 }}>
          <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,100 L0,100 Z" fill="#F5F9FF"/>
          </svg>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          CLIENT MARQUEE
      ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor:"#F5F9FF",py:6,overflow:"hidden" }}>
        <Typography variant="overline" sx={{ display:"block",textAlign:"center",mb:3,
          color:"#1565C0",fontWeight:700,letterSpacing:"0.18em",fontSize:"0.78rem" }}>
          TRUSTED BY LEADING ORGANIZATIONS
        </Typography>
        <Box sx={{ position:"relative",overflow:"hidden",
          "&::before,&::after":{ content:'""',position:"absolute",top:0,bottom:0,width:120,zIndex:2 },
          "&::before":{ left:0,background:"linear-gradient(90deg,#F5F9FF,transparent)" },
          "&::after":{ right:0,background:"linear-gradient(-90deg,#F5F9FF,transparent)" },
        }}>
          <Box sx={{ display:"flex",gap:5,width:"max-content",
            animation:`${marquee} 25s linear infinite`,
            "&:hover":{ animationPlayState:"paused" } }}>
            {[...CLIENTS,...CLIENTS].map((c,i)=>(
              <Box key={i} sx={{ px:3,py:1.5,borderRadius:2,whiteSpace:"nowrap",
                bgcolor:"white",border:"1px solid #E3F2FD",
                boxShadow:"0 2px 12px rgba(13,71,161,0.06)",cursor:"default",
                transition:"all 0.3s ease",
                "&:hover":{ transform:"translateY(-6px) scale(1.05)",
                  boxShadow:"0 12px 40px rgba(13,71,161,0.18)",border:"1px solid #90CAF9" } }}>
                <Typography sx={{ fontWeight:700,color:"#0D47A1",fontSize:"0.88rem",letterSpacing:"0.04em" }}>{c}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          STATS — 3D hover counters
      ═══════════════════════════════════════════════════════════════ */}
      <Box ref={statsRef} sx={{ bgcolor:"#F5F9FF",pb:12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {STATS.map((s,i)=>(
              <Grid item xs={6} md={3} key={i}>
                  <Box sx={{ textAlign:"center",p:4,borderRadius:4,bgcolor:"white",
                    border:"1px solid #E3F2FD",
                    boxShadow:"0 4px 24px rgba(13,71,161,0.07)",
                    transition:"all 0.4s ease",
                    opacity:1,
                    transform:"translateY(0)",
                    transitionDelay:`${i*0.12}s`,
                    transition:"all 0.3s ease",cursor:"default",
                  "&:hover":{ boxShadow:"0 24px 80px rgba(13,71,161,0.22)",transform:"translateY(-8px) scale(1.02)" } }}>
                    <Box sx={{ color:s.color,mb:1.5 }}>{s.icon}</Box>
                    <Typography sx={{ fontWeight:900,fontSize:{xs:"2.2rem",md:"3rem"},
                      color:"#0D47A1",lineHeight:1,mb:0.5,
                      textShadow:`0 0 30px ${s.color}44` }}>
                      <Counter target={s.n} inView={statsInView} />
                    </Typography>
                    <Typography sx={{ color:"#546e7a",fontWeight:600,fontSize:"0.82rem",textTransform:"uppercase",letterSpacing:"0.08em" }}>
                      {s.label}
                    </Typography>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          SERVICES — 3D tilt cards
      ═══════════════════════════════════════════════════════════════ */}
      <Box ref={servicesRef} sx={{ py:14,bgcolor:"white",position:"relative",overflow:"hidden" }}>
        {/* bg decoration */}
        <Box sx={{ position:"absolute",top:-200,right:-200,width:600,height:600,
          borderRadius:"50%",opacity:0.03,
          background:"radial-gradient(circle,#1565C0,transparent)" }} />
        <Container maxWidth="lg">
          <Box sx={{ textAlign:"center",mb:10 }}>
            <Typography variant="overline" sx={{ color:"#1565C0",fontWeight:700,fontSize:"0.82rem",letterSpacing:"0.18em" }}>WHAT WE OFFER</Typography>
            <Typography variant="h2" sx={{ mt:2,mb:2,color:"#0A1929",fontWeight:900,fontSize:{xs:"2rem",md:"3.2rem"} }}>
              Comprehensive Technology Solutions
            </Typography>
            <Typography sx={{ color:"#546e7a",maxWidth:600,mx:"auto",fontSize:"1rem",lineHeight:1.8 }}>
              From training to deployment — full‑stack technology partner for your growth
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {SERVICES.map((s,i)=>(
              <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ height:"100%",p:4,borderRadius:4,
                    border:"1px solid #E3F2FD",bgcolor:"white",
                    position:"relative",overflow:"hidden",cursor:"pointer",
                    transition:"all 0.4s cubic-bezier(0.4,0,0.2,1)",
                    opacity:1,
                    transform:"translateY(0)",
                    transitionDelay:`${i*0.1}s`,
                    "&::before":{ content:'""',position:"absolute",top:0,left:0,right:0,height:"3px",
                      background:`linear-gradient(90deg,${s.color},${s.color}88)`,
                      boxShadow:`0 0 12px ${s.color}` },
                    "&:hover":{
                      boxShadow:`0 32px 80px ${s.color}22`,
                      border:`1px solid ${s.color}44`,
                      "& .svc-icon":{ transform:"scale(1.15) rotate(8deg)",
                        background:`linear-gradient(135deg,${s.color},${s.color}cc)`,color:"white",
                        boxShadow:`0 8px 30px ${s.color}60` },
                    } }}>
                    <Box className="svc-icon" sx={{ width:68,height:68,borderRadius:3,
                      background:`${s.color}12`,color:s.color,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      mb:2.5,transition:"all 0.35s ease" }}>
                      {s.icon}
                    </Box>
                    <Typography variant="h6" sx={{ mb:1.5,fontWeight:800,color:"#0A1929" }}>{s.title}</Typography>
                    <Typography variant="body2" sx={{ color:"#546e7a",mb:2.5,lineHeight:1.7 }}>{s.desc}</Typography>
                    <Box sx={{ display:"flex",flexWrap:"wrap",gap:0.8 }}>
                      {s.tags.map((t,j)=>(
                        <Chip key={j} label={t} size="small"
                          sx={{ bgcolor:`${s.color}10`,color:s.color,fontWeight:700,fontSize:"0.7rem",
                            border:`1px solid ${s.color}25` }} />
                      ))}
                    </Box>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          WHY CHOOSE US — dark 3D glassmorphism
      ═══════════════════════════════════════════════════════════════ */}
      <Box ref={whyRef} sx={{ py:14,position:"relative",overflow:"hidden",
        background:"linear-gradient(150deg,#020818 0%,#0a1628 40%,#0D47A1 100%)" }}>
        <ParticleCanvas />
        <Box sx={{ position:"absolute",inset:0,opacity:0.04,
          backgroundImage:"radial-gradient(circle,rgba(147,197,253,0.6) 1px,transparent 1px)",
          backgroundSize:"40px 40px" }} />
        <Container maxWidth="lg" sx={{ position:"relative",zIndex:2 }}>
          <Box sx={{ textAlign:"center",mb:10 }}>
            <Typography variant="overline" sx={{ color:"#93C5FD",fontWeight:700,fontSize:"0.82rem",letterSpacing:"0.18em" }}>WHY QUANTUM IT AI LLC</Typography>
            <Typography variant="h2" sx={{ mt:2,color:"white",fontWeight:900,fontSize:{xs:"2rem",md:"3.2rem"} }}>
              Your Success is Our Mission
            </Typography>
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {[
              { icon:<TrendingUpIcon sx={{fontSize:52}}/>,  title:"Proven Track Record",  stat:"98%", sl:"Success Rate",    desc:"850+ successful projects across 20+ industries with consistent excellence." },
              { icon:<SecurityIcon sx={{fontSize:52}}/>,    title:"Enterprise Security",   stat:"100%",sl:"Compliance",      desc:"ISO 27001 certified with bank‑grade security and data protection protocols." },
              { icon:<SpeedIcon sx={{fontSize:52}}/>,       title:"Agile Delivery",        stat:"40%", sl:"Faster",          desc:"2-week sprint cycles with CI/CD and rapid, reliable deployment." },
              { icon:<SupportAgentIcon sx={{fontSize:52}}/>,title:"24/7 Support",          stat:"4.9★",sl:"Rating",          desc:"Round-the-clock support with < 1 hour guaranteed response time." },
            ].map((item,i)=>(
              <Grid item xs={12} sm={6} md={3} key={i}>
                  <Box sx={{ p:4,borderRadius:4,textAlign:"center",
                    background:"rgba(255,255,255,0.05)",backdropFilter:"blur(24px)",
                    border:"1px solid rgba(147,197,253,0.12)",
                    opacity:1,
                    transform:"translateY(0)",
                    transition:"all 0.5s ease",transitionDelay:`${i*0.12}s`,
                    "&:hover":{ background:"rgba(255,255,255,0.1)",
                      border:"1px solid rgba(147,197,253,0.3)",
                      boxShadow:"0 24px 80px rgba(0,0,0,0.5)",
                      "& .why-icon":{ color:"#93C5FD",transform:"scale(1.2) rotate(10deg)" } } }}>
                    <Box className="why-icon" sx={{ color:"#60A5FA",mb:2,transition:"all 0.3s ease" }}>{item.icon}</Box>
                    <Typography sx={{ color:"white",fontWeight:900,fontSize:"2.8rem",lineHeight:1,
                      textShadow:"0 0 30px rgba(147,197,253,0.6)",mb:0.3 }}>{item.stat}</Typography>
                    <Typography sx={{ color:"#93C5FD",fontSize:"0.72rem",fontWeight:600,letterSpacing:"0.12em",
                      textTransform:"uppercase",mb:2 }}>{item.sl}</Typography>
                    <Typography variant="h6" sx={{ color:"white",fontWeight:800,mb:1.5,fontSize:"1rem" }}>{item.title}</Typography>
                    <Typography variant="body2" sx={{ color:"rgba(255,255,255,0.65)",lineHeight:1.7,fontSize:"0.85rem" }}>{item.desc}</Typography>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
        <Box sx={{ position:"absolute",bottom:-2,left:0,right:0,zIndex:3 }}>
          <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0,40 C480,80 960,0 1440,40 L1440,80 L0,80 Z" fill="#F5F9FF"/>
          </svg>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════════════════════════════════ */}
      <Box ref={testimonialsRef} sx={{ py:14,bgcolor:"#F5F9FF" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign:"center",mb:10 }}>
            <Typography variant="overline" sx={{ color:"#1565C0",fontWeight:700,fontSize:"0.82rem",letterSpacing:"0.18em" }}>CLIENT TESTIMONIALS</Typography>
            <Typography variant="h2" sx={{ mt:2,color:"#0A1929",fontWeight:900,fontSize:{xs:"2rem",md:"3.2rem"} }}>
              Trusted by Industry Leaders
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {TESTIMONIALS.map((t,i)=>(
              <Grid item xs={12} md={4} key={i}>
                  <Box sx={{ p:4,borderRadius:4,bgcolor:"white",height:"100%",
                    border:"1px solid #E3F2FD",
                    boxShadow:"0 4px 24px rgba(13,71,161,0.06)",
                    position:"relative",overflow:"hidden",
                    opacity:1,
                    transform:"translateY(0) scale(1)",
                    transition:"all 0.5s ease",transitionDelay:`${i*0.15}s`,
                    "&:hover":{ boxShadow:"0 24px 80px rgba(13,71,161,0.18)" } }}>
                    <FormatQuoteIcon sx={{ position:"absolute",top:16,right:16,fontSize:70,
                      color:"rgba(13,71,161,0.05)" }} />
                    <Rating value={5} readOnly size="small" sx={{ mb:2,
                      "& .MuiRating-iconFilled":{ color:"#1565C0" } }} />
                    <Typography sx={{ mb:3,lineHeight:1.8,fontStyle:"italic",color:"#37474F",fontSize:"0.92rem" }}>
                      "{t.text}"
                    </Typography>
                    <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
                      <Avatar sx={{ bgcolor:"linear-gradient(135deg,#0D47A1,#1976D2)",
                        background:"linear-gradient(135deg,#0D47A1,#1976D2)",
                        width:50,height:50,fontWeight:800,fontSize:"1rem",
                        boxShadow:"0 4px 16px rgba(13,71,161,0.4)" }}>{t.av}</Avatar>
                      <Box>
                        <Typography sx={{ fontWeight:800,color:"#0A1929",fontSize:"0.95rem" }}>{t.name}</Typography>
                        <Typography sx={{ color:"#546e7a",fontSize:"0.78rem" }}>{t.pos}</Typography>
                      </Box>
                    </Box>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          CTA
      ═══════════════════════════════════════════════════════════════ */}
      <Box ref={ctaRef} sx={{ py:14,position:"relative",overflow:"hidden",
        background:"linear-gradient(-45deg,#020818,#0a1628,#0D47A1,#1a237e,#020818)",
        backgroundSize:"400% 400%",animation:`${gradientShift} 10s ease infinite` }}>
        <ParticleCanvas />
        <Container maxWidth="md" sx={{ position:"relative",zIndex:2,textAlign:"center" }}>
          <Box sx={{ opacity:1,transform:"translateY(0)",
            transition:"all 0.9s ease" }}>
            <Typography variant="h2" sx={{ color:"white",fontWeight:900,mb:2,
              fontSize:{xs:"2rem",md:"3.8rem"},letterSpacing:"-0.02em" }}>
              Ready to{" "}
              <Box component="span" sx={{
                background:"linear-gradient(90deg,#93C5FD,#fff,#93C5FD)",
                backgroundSize:"200% auto",backgroundClip:"text",
                WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
                animation:`${shimmer} 3s linear infinite` }}>
                Transform?
              </Box>
            </Typography>
            <Typography sx={{ color:"rgba(255,255,255,0.8)",mb:5,
              maxWidth:580,mx:"auto",lineHeight:1.8,fontSize:"1.05rem" }}>
              Let's discuss how Quantum IT AI LLC can accelerate your growth and technology goals.
            </Typography>
            <Box sx={{ display:"flex",gap:2,justifyContent:"center",flexWrap:"wrap" }}>
              <Button component={Link} to="/contact" variant="contained" size="large" endIcon={<EastIcon />}
                sx={{ px:5,py:2,fontSize:"1.05rem",fontWeight:800,
                  background:"linear-gradient(135deg,#fff,#E3F2FD)",color:"#0D47A1",
                  borderRadius:"50px",boxShadow:"0 8px 40px rgba(0,0,0,0.5)",
                  "&:hover":{transform:"translateY(-6px) scale(1.03)",
                    boxShadow:"0 20px 60px rgba(0,0,0,0.6)"} }}>
                Schedule a Consultation
              </Button>
              <Button component={Link} to="/services" variant="outlined" size="large"
                sx={{ px:5,py:2,fontSize:"1.05rem",fontWeight:700,
                  borderColor:"rgba(147,197,253,0.6)",color:"white",
                  borderRadius:"50px",borderWidth:2,backdropFilter:"blur(10px)",
                  background:"rgba(255,255,255,0.04)",
                  "&:hover":{borderWidth:2,background:"rgba(255,255,255,0.1)",transform:"translateY(-6px)"} }}>
                View Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
